export type GeometryLod = 'marker' | 'L2' | 'L1' | 'L0'
export type DecalLod = 'none' | 'route' | 'full'

export type TrainRenderProfile = {
  geometry: GeometryLod
  decals: DecalLod
}

export type TrainMapPosition = readonly [longitude: number, latitude: number]
export type ProjectedPoint = { x: number; y: number }
export type TrainPointProjector = (position: TrainMapPosition) => ProjectedPoint | null
export type ProjectedViewport = { width: number; height: number }

export type TrainRenderLodInput = {
  vehicleId: string
  anchor: TrainMapPosition
  /** Bearing in degrees clockwise from north. */
  heading: number
  /** Compatible with the `{x, y}` result returned by `map.project`. */
  project: TrainPointProjector
  viewport?: ProjectedViewport
  watched?: boolean
  boarded?: boolean
}

export const PROJECTED_CAR_LENGTH_METRES = 18.35
// Require two additional MapLibre zoom steps before entering every mesh tier. Locally,
// each zoom step doubles a car's projected length, so multiplying the original 3/12/35 px
// thresholds by four keeps trains as route markers until the camera is roughly 4x closer.
export const TRAIN_RENDER_LOD_THRESHOLDS_PX = Object.freeze({
  L2: 12,
  L1: 48,
  L0: 140,
})
export const TRAIN_RENDER_LOD_HYSTERESIS = 0.2

const MARKER_PROFILE: TrainRenderProfile = Object.freeze({ geometry: 'marker', decals: 'route' })
const L2_PROFILE: TrainRenderProfile = Object.freeze({ geometry: 'L2', decals: 'none' })
const L1_PROFILE: TrainRenderProfile = Object.freeze({ geometry: 'L1', decals: 'route' })
const L0_PROFILE: TrainRenderProfile = Object.freeze({ geometry: 'L0', decals: 'full' })

export function trainRenderProfileForGeometry(geometry: GeometryLod): TrainRenderProfile {
  if (geometry === 'L0') return L0_PROFILE
  if (geometry === 'L1') return L1_PROFILE
  if (geometry === 'L2') return L2_PROFILE
  return MARKER_PROFILE
}

function rawGeometryLod(projectedCarSizePx: number): GeometryLod {
  if (!Number.isFinite(projectedCarSizePx) || projectedCarSizePx < TRAIN_RENDER_LOD_THRESHOLDS_PX.L2) {
    return 'marker'
  }
  if (projectedCarSizePx < TRAIN_RENDER_LOD_THRESHOLDS_PX.L1) return 'L2'
  if (projectedCarSizePx <= TRAIN_RENDER_LOD_THRESHOLDS_PX.L0) return 'L1'
  return 'L0'
}

/**
 * Resolves a projected length to a tier. Passing the vehicle's previous tier applies
 * symmetric enter/exit hysteresis; omitting it gives the exact raw policy bands.
 */
export function geometryLodForProjectedSize(
  projectedCarSizePx: number,
  previous?: GeometryLod,
  hysteresis = TRAIN_RENDER_LOD_HYSTERESIS,
): GeometryLod {
  if (!Number.isFinite(projectedCarSizePx) || projectedCarSizePx < 0) return 'marker'
  if (previous === undefined) return rawGeometryLod(projectedCarSizePx)

  const margin = Number.isFinite(hysteresis) && hysteresis >= 0 && hysteresis < 1
    ? hysteresis
    : TRAIN_RENDER_LOD_HYSTERESIS
  const lower = 1 - margin
  const upper = 1 + margin

  if (previous === 'marker') {
    if (projectedCarSizePx < TRAIN_RENDER_LOD_THRESHOLDS_PX.L2 * upper) return 'marker'
  } else if (previous === 'L2') {
    if (projectedCarSizePx > TRAIN_RENDER_LOD_THRESHOLDS_PX.L2 * lower
      && projectedCarSizePx < TRAIN_RENDER_LOD_THRESHOLDS_PX.L1 * upper) return 'L2'
  } else if (previous === 'L1') {
    if (projectedCarSizePx >= TRAIN_RENDER_LOD_THRESHOLDS_PX.L1 * lower
      && projectedCarSizePx < TRAIN_RENDER_LOD_THRESHOLDS_PX.L0 * upper) return 'L1'
  } else if (projectedCarSizePx > TRAIN_RENDER_LOD_THRESHOLDS_PX.L0 * lower) {
    return 'L0'
  }

  return rawGeometryLod(projectedCarSizePx)
}

const EARTH_RADIUS_METRES = 6_371_008.8
const radians = (degrees: number) => degrees * Math.PI / 180
const degrees = (radiansValue: number) => radiansValue * 180 / Math.PI

function offsetAlongHeading(
  [longitude, latitude]: TrainMapPosition,
  heading: number,
): TrainMapPosition | null {
  if (!Number.isFinite(longitude) || !Number.isFinite(latitude)
    || latitude < -90 || latitude > 90 || !Number.isFinite(heading)) return null

  const angularDistance = PROJECTED_CAR_LENGTH_METRES / EARTH_RADIUS_METRES
  const bearing = radians(heading)
  const latitude1 = radians(latitude)
  const longitude1 = radians(longitude)
  const latitude2 = Math.asin(
    Math.sin(latitude1) * Math.cos(angularDistance)
      + Math.cos(latitude1) * Math.sin(angularDistance) * Math.cos(bearing),
  )
  const longitude2 = longitude1 + Math.atan2(
    Math.sin(bearing) * Math.sin(angularDistance) * Math.cos(latitude1),
    Math.cos(angularDistance) - Math.sin(latitude1) * Math.sin(latitude2),
  )

  return [((degrees(longitude2) + 540) % 360) - 180, degrees(latitude2)]
}

function finitePoint(point: ProjectedPoint | null): point is ProjectedPoint {
  return point !== null && Number.isFinite(point.x) && Number.isFinite(point.y)
}

function intersectsViewport(
  anchor: ProjectedPoint,
  offset: ProjectedPoint,
  viewport: ProjectedViewport,
): boolean {
  if (!Number.isFinite(viewport.width) || !Number.isFinite(viewport.height)
    || viewport.width < 0 || viewport.height < 0) return false

  // Liang-Barsky clips the actual car segment, rather than accepting any segment
  // whose independent x/y ranges happen to overlap the viewport near a corner.
  const dx = offset.x - anchor.x
  const dy = offset.y - anchor.y
  let enter = 0
  let exit = 1
  const edges = [
    [-dx, anchor.x],
    [dx, viewport.width - anchor.x],
    [-dy, anchor.y],
    [dy, viewport.height - anchor.y],
  ] as const

  for (const [direction, distance] of edges) {
    if (direction === 0) {
      if (distance < 0) return false
      continue
    }
    const ratio = distance / direction
    if (direction < 0) enter = Math.max(enter, ratio)
    else exit = Math.min(exit, ratio)
    if (enter > exit) return false
  }
  return true
}

/** Returns the projected length of one 18.35 m car, or `null` when it cannot be seen. */
export function projectedCarSizePx(
  anchor: TrainMapPosition,
  heading: number,
  project: TrainPointProjector,
  viewport?: ProjectedViewport,
): number | null {
  const offset = offsetAlongHeading(anchor, heading)
  const perpendicularOffset = offsetAlongHeading(anchor, heading + 90)
  if (offset === null || perpendicularOffset === null) return null

  try {
    const projectedAnchor = project(anchor)
    const projectedOffset = project(offset)
    const projectedPerpendicularOffset = project(perpendicularOffset)
    if (!finitePoint(projectedAnchor) || !finitePoint(projectedOffset)
      || !finitePoint(projectedPerpendicularOffset)) return null
    if (viewport && !intersectsViewport(projectedAnchor, projectedOffset, viewport)) return null

    // The rail-direction segment alone can be severely foreshortened when a north/south
    // train points into a pitched camera. Treat the along-track and perpendicular probes as
    // a local 2D projection basis and use its largest singular value. That is the projected
    // diameter of a conservative car-sized bound: it follows distance/perspective, but cannot
    // suddenly demote a prominent car merely because its heading lines up with the camera.
    const alongX = projectedOffset.x - projectedAnchor.x
    const alongY = projectedOffset.y - projectedAnchor.y
    const acrossX = projectedPerpendicularOffset.x - projectedAnchor.x
    const acrossY = projectedPerpendicularOffset.y - projectedAnchor.y
    const alongSquared = alongX * alongX + alongY * alongY
    const acrossSquared = acrossX * acrossX + acrossY * acrossY
    const dot = alongX * acrossX + alongY * acrossY
    const discriminant = Math.hypot(alongSquared - acrossSquared, 2 * dot)
    return Math.sqrt(Math.max(0, (alongSquared + acrossSquared + discriminant) / 2))
  } catch {
    return null
  }
}

/** Stateful only at the per-vehicle hysteresis boundary; projection and tier rules are pure. */
export class TrainRenderLodPolicy {
  readonly #previousGeometry = new Map<string, GeometryLod>()

  get trackedVehicleCount(): number {
    return this.#previousGeometry.size
  }

  resolve(input: TrainRenderLodInput): TrainRenderProfile {
    const size = projectedCarSizePx(input.anchor, input.heading, input.project, input.viewport)
    let geometry: GeometryLod

    if (size === null) {
      this.#previousGeometry.delete(input.vehicleId)
      geometry = 'marker'
    } else {
      geometry = geometryLodForProjectedSize(size, this.#previousGeometry.get(input.vehicleId))
      this.#previousGeometry.set(input.vehicleId, geometry)
    }

    if (input.watched || input.boarded) return L0_PROFILE
    return trainRenderProfileForGeometry(geometry)
  }

  /** Removes hysteresis records not represented in the current visible train set. */
  cleanup(activeVisibleVehicleIds: Iterable<string>): number {
    const active = new Set(activeVisibleVehicleIds)
    let removed = 0
    for (const vehicleId of this.#previousGeometry.keys()) {
      if (active.has(vehicleId)) continue
      this.#previousGeometry.delete(vehicleId)
      removed += 1
    }
    return removed
  }

  clear(): void {
    this.#previousGeometry.clear()
  }
}
